import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { FoodBowl } from './FoodBowl';
import type { CareEvent } from '../care/CareRecord';
import type { FoodState } from '../care/Household';

/**
 * The food loop: hunger climbs with time (also while the app is closed),
 * the shop (a panel, from the 🎒) sells bags, tapping the bowl empties a bag into it, and a
 * hungry dog walks over and eats — or whines at an empty bowl. State is saved
 * in localStorage so you really do have to come back and refill.
 */
const HUNGER_PERIOD = 240; // seconds from fed to starving
const EAT_AT = 0.45; // hunger level at which she goes for the bowl
const BITE_EVERY = 0.7;
const BITE_FILL = 0.025; // one full bowl ≈ 2–3 meals
const BITE_HUNGER = 0.06;
const REACH = 0.7; // where the dog stands relative to the bowl (head lands over it)

const _p = new THREE.Vector3();

export class Feeding {
  bags = 0;
  treats = 0;
  /** the last day a holiday put extra treats in the jar (YYYY-MM-DD); synced, so it happens once per license */
  treatDay: string | null = null;
  onMessage?: (text: string) => void;
  onStatus?: (text: string) => void;
  onCare?: (ev: CareEvent) => void;
  private complainedEmpty = false;

  private state: 'idle' | 'walking' | 'eating' = 'idle';
  private biteTimer = 0;
  private complainAt = 0;
  private saveTimer = 0;

  private saveKey: string;

  constructor(
    private dog: Dog,
    private bowl: FoodBowl,
  ) {
    this.saveKey = `lunaloo.food.${dog.profile.id}`;
    this.load();
  }

  onShopOpen?: () => void;

  /** the shop panel (there's no shack any more: she used to walk straight through it) */
  openShop(): void {
    this.onShopOpen?.();
  }

  /** the till: return false to refuse the sale (not enough credits) */
  onBuy?: (what: 'kibble' | 'treats' | 'torch') => boolean;
  /** a torch for the yard at night (one-off) */
  torch = false;

  buy(what: 'kibble' | 'treats' | 'torch'): void {
    if (what === 'torch' && this.torch) return;
    if (this.onBuy && !this.onBuy(what)) return;
    if (what === 'torch') {
      this.torch = true;
      this.onMessage?.('a torch 🔦 — the yard lights up for you at night');
      this.save();
      return;
    }
    if (what === 'kibble') this.bags++;
    else this.treats += 3;
    this.onMessage?.(what === 'kibble' ? `a bag of kibble (${this.bags} in the cupboard)` : `3 treats (${this.treats} now)`);
    this.save();
  }

  /** hand her a treat: true if there was one */
  giveTreat(): boolean {
    if (this.treats <= 0) {
      this.onMessage?.('no treats — the shop (🎒) sells them');
      return false;
    }
    this.treats--;
    this.dog.hunger = Math.max(0, this.dog.hunger - 0.04);
    this.dog.bite();
    this.dog.excitement = 1;
    this.save();
    return true;
  }

  tapBowl(): void {
    if (this.bags <= 0) {
      this.onMessage?.('no kibble — buy a bag');
      this.onShopOpen?.();
      return;
    }
    if (this.bowl.fill > 0.85) {
      this.onMessage?.('the bowl is already full');
      return;
    }
    this.bags--;
    this.bowl.fill = 1;
    this.bowl.update();
    this.onMessage?.('bowl filled');
    this.complainedEmpty = false;
    this.onCare?.('feed');
    this.save();
    // a full bowl gets her attention even if she isn't hungry yet
    this.dog.notice(this.bowl.root.position);
  }

  update(dt: number, cameraPos: THREE.Vector3): void {
    const dog = this.dog;
    dog.hunger = Math.min(1, dog.hunger + dt / HUNGER_PERIOD);

    const bowlPos = this.bowl.root.position;
    const near = dog.root.position.distanceTo(bowlPos) < REACH + 0.25;

    switch (this.state) {
      case 'idle':
        if (dog.hunger > EAT_AT && dog.idle && dog.time > this.complainAt) {
          if (this.bowl.fill > 0.02) {
            dog.goTo(this.bowl.approachPoint(dog.root.position, REACH, _p));
            this.state = 'walking';
          } else if (near) {
            // hungry, at an empty bowl: look at it, look at you, whine
            dog.complain(bowlPos, cameraPos);
            if (!this.complainedEmpty) {
              this.complainedEmpty = true;
              this.onCare?.('emptyBowl');
            }
            this.complainAt = dog.time + 12 + Math.random() * 10;
          } else {
            dog.goTo(this.bowl.approachPoint(dog.root.position, REACH, _p));
            this.state = 'walking';
          }
        }
        break;
      case 'walking':
        if (!dog.moving) {
          if (near && this.bowl.fill > 0.02) {
            dog.startEating(bowlPos);
            this.state = 'eating';
            this.biteTimer = 0;
          } else {
            this.state = 'idle';
            if (near) this.complainAt = 0; // complain right away
            else this.complainAt = dog.time + 5; // got interrupted; try again shortly
          }
        }
        break;
      case 'eating':
        this.biteTimer += dt;
        if (this.biteTimer > BITE_EVERY) {
          this.biteTimer = 0;
          this.bowl.fill = Math.max(0, this.bowl.fill - BITE_FILL);
          this.bowl.update();
          dog.hunger = Math.max(0, dog.hunger - BITE_HUNGER);
          dog.bite();
        }
        if (!dog.eating || dog.hunger < 0.08 || this.bowl.fill <= 0.02) {
          dog.stopEating(cameraPos);
          this.state = 'idle';
          this.complainAt = dog.time + (this.bowl.fill <= 0.02 && dog.hunger > EAT_AT ? 2 : 20);
          this.save();
        }
        break;
    }

    this.saveTimer += dt;
    if (this.saveTimer > 5) {
      this.saveTimer = 0;
      this.save();
    }
    this.onStatus?.(this.statusText());
  }

  /** for sync */
  snapshot(): FoodState {
    return { bags: this.bags, treats: this.treats, treatDay: this.treatDay ?? undefined, torch: this.torch, fill: this.bowl.fill, hunger: this.dog.hunger, at: Date.now() };
  }

  /** a newer copy from another phone wins */
  applyRemote(f: FoodState): void {
    const mine = this.lastSavedAt;
    if (f.at <= mine) return;
    this.bags = f.bags;
    this.treats = f.treats ?? this.treats;
    this.treatDay = f.treatDay ?? this.treatDay;
    this.torch = this.torch || !!f.torch; // once bought, bought everywhere
    this.bowl.fill = f.fill;
    this.bowl.update();
    const away = Math.min(86400, (Date.now() - f.at) / 1000);
    this.dog.hunger = Math.min(1, f.hunger + away / HUNGER_PERIOD);
    this.lastSavedAt = f.at;
    this.save();
  }

  private lastSavedAt = 0;

  /** a holiday's extra treats: once per day, and the day syncs so the second phone doesn't grant again */
  holidayTreats(day: string, n: number): boolean {
    if (this.treatDay === day) return false;
    this.treatDay = day;
    this.treats += n;
    this.save();
    return true;
  }

  private statusText(): string {
    const bowl = '▮'.repeat(Math.round(this.bowl.fill * 5)) + '▯'.repeat(5 - Math.round(this.bowl.fill * 5));
    const h = this.dog.hunger;
    const hungry = h > 0.8 ? 'starving' : h > EAT_AT ? 'hungry' : h > 0.2 ? 'peckish' : 'full';
    return `bowl ${bowl}   bags ${this.bags}   treats ${this.treats}   ${hungry}`;
  }

  private save(): void {
    try {
      this.lastSavedAt = Date.now();
      localStorage.setItem(this.saveKey, JSON.stringify({ bags: this.bags, treats: this.treats, treatDay: this.treatDay, torch: this.torch, fill: this.bowl.fill, hunger: this.dog.hunger, at: this.lastSavedAt }));
    } catch {
      /* private mode etc. */
    }
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(this.saveKey);
      if (!raw) {
        this.bags = 1; // a starter bag so the first session teaches the loop
        return;
      }
      const s = JSON.parse(raw) as { bags: number; treats?: number; treatDay?: string | null; fill: number; hunger: number; at: number };
      this.bags = s.bags;
      this.treats = s.treats ?? 0;
      this.treatDay = s.treatDay ?? null;
      this.bowl.fill = s.fill;
      this.bowl.update();
      this.lastSavedAt = s.at;
      // hunger kept climbing while you were away, up to a day's worth (she'd have found something)
      const away = Math.min(86400, (Date.now() - s.at) / 1000);
      this.dog.hunger = Math.min(1, s.hunger + away / HUNGER_PERIOD);
    } catch {
      this.bags = 1;
    }
  }
}
